import { logger } from '../../services/logger';
import React, { useState, useEffect } from 'react';
import { Market } from '../../types';
import { toast } from 'react-hot-toast';
import { supabase } from '../../lib/db';
import { useAuth } from '../../lib/auth';
import { AlertTriangle } from 'lucide-react';

// Define expected props
interface MarketCancellerProps {
  market: Market;
  marketId?: string;
  onCancel?: (reason: string) => void;
  onRefreshMarketData?: () => void; // New callback to trigger market data refresh
}

export function MarketCanceller({ market, marketId, onCancel, onRefreshMarketData }: MarketCancellerProps) {
  const [cancelReason, setCancelReason] = useState('');
  const [isProcessing, setIsProcessing] = useState(false);
  const [isProcessingRefunds, setIsProcessingRefunds] = useState(false);
  const [isCancelled, setIsCancelled] = useState(market.status === 'cancelled');
  const [marketData, setMarketData] = useState<Market>(market);
  const { user } = useAuth();

  // Check if user can cancel this market
  const isAdmin = user?.app_metadata?.role === 'admin' || user?.app_metadata?.role === 'superuser';
  const isCreator = user?.id === market.created_by;
  
  // Determine if user has permission to cancel
  const canCancel = marketData.is_private
    ? isCreator // Only creator can cancel private markets
    : isAdmin;  // Only admins can cancel public markets

  // Permission explanation message
  const permissionMessage = marketData.is_private
    ? "Only the creator can cancel private markets."
    : "Only administrators can cancel public markets.";

  // Update local state when the market prop changes
  useEffect(() => {
    setMarketData(market);
    setIsCancelled(market.status === 'cancelled');
  }, [market]);

  // Function to fetch the latest market data
  const fetchMarketData = async () => {
    if (!marketId) return;
    
    try {
      const { data, error } = await supabase
        .from('markets')
        .select('*')
        .eq('id', marketId)
        .single();
        
      if (error) {
        logger.error('Error fetching market data:', error);
        return;
      }
      
      if (data) {
        setMarketData(data as Market);
        setIsCancelled(data.status === 'cancelled');
        
        // Call the parent refresh callback if provided
        if (onRefreshMarketData) {
          onRefreshMarketData();
        }
      }
    } catch (error) {
      logger.error('Error fetching market data:', error);
    }
  };

  const handleCancelMarket = async () => {
    if (!marketId || !market) {
      toast.error('Market ID is required');
      return;
    }

    // Check permissions again to be safe
    if (!canCancel) {
      toast.error(permissionMessage);
      return;
    }
    
    try {
      setIsProcessing(true);
      
      // Call the cancel_market RPC function
      const { error: rpcError } = await supabase.rpc('cancel_market', { 
        p_market_id: marketId,
        p_cancellation_reason: cancelReason.trim() || 'Cancelled by admin'
      });
      
      if (rpcError) {
        logger.error('Error cancelling market:', rpcError);
        throw rpcError;
      }
      
      toast.success('Market cancelled successfully. You can now process refunds for all trades.');
      
      // Update local state immediately
      setIsCancelled(true);
      
      // Also update the market data with cancellation info
      setMarketData({
        ...marketData,
        status: 'cancelled',
        cancellation_reason: cancelReason.trim() || 'Cancelled by admin',
        cancelled_at: new Date().toISOString()
      });
      
      // Fetch the updated market data
      logger.info("Fetching market data after cancellation...");
      await fetchMarketData();
      logger.info("Market data fetched after cancellation");
      
      // Call the onCancel callback if provided
      if (onCancel) {
        onCancel(cancelReason.trim() || 'Cancelled by admin');
      }
      
      // Force a reload of the page if needed
      if (onRefreshMarketData) {
        logger.info("Calling parent refresh function...");
        onRefreshMarketData();
      }
      
    } catch (error: unknown) {
      logger.error('Error cancelling market:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error('Failed to cancel market: ' + errorMessage);
    } finally {
      setIsProcessing(false);
    }
  };

  const handleProcessRefunds = async () => {
    if (!marketId) {
      toast.error('Market ID is required');
      return;
    }

    // Only admins can process refunds
    if (!isAdmin) {
      toast.error('Only administrators can process refunds.');
      return;
    }
    
    try {
      setIsProcessingRefunds(true);
      const toastId = toast.loading('Processing refunds...');
      
      // Call the process_market_cancellation_refunds RPC function
      const { data, error: rpcError } = await supabase.rpc('process_market_cancellation_refunds', { 
        p_market_id: marketId
      });
      
      if (rpcError) {
        logger.error('Error processing refunds:', rpcError);
        throw rpcError;
      }
      
      toast.dismiss(toastId);
      toast.success(`Successfully processed refunds for ${data} trades.`);
      
      // Refresh market data to reflect any changes
      await fetchMarketData();
      
    } catch (error: unknown) {
      logger.error('Error processing refunds:', error);
      const errorMessage = error instanceof Error ? error.message : String(error);
      toast.error('Failed to process refunds: ' + errorMessage);
    } finally {
      setIsProcessingRefunds(false);
    }
  };

  // If market is already cancelled, show cancellation details and refund option
  if (isCancelled) {
    logger.info("Market is cancelled, showing refund UI");
    logger.info("Is admin:", isAdmin);
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Cancel Market {marketId && `(ID: ${marketId})`}</h2>
        <div className="mb-6">
          <div className="bg-amber-50 border border-amber-200 p-4 rounded-md mb-4">
            <p className="text-amber-800">This market has been cancelled.</p>
            <p className="text-amber-800 mt-2"><span className="font-medium">Status:</span> {marketData.status}</p>
            {marketData.cancellation_reason && (
              <p className="text-amber-800 mt-2"><span className="font-medium">Reason:</span> {marketData.cancellation_reason}</p>
            )}
            {marketData.cancelled_at && (
              <p className="text-amber-800 mt-2"><span className="font-medium">Cancelled at:</span> {new Date(marketData.cancelled_at).toLocaleString()}</p>
            )}
          </div>
          
          {isAdmin ? (
            <div className="mt-6">
              <h3 className="text-lg font-medium mb-2">Process Refunds</h3>
              <p className="text-gray-600 mb-4">
                Issue refunds for all trades in this cancelled market. This will refund both the trade amount and service fee back to each trader.
              </p>
              <button 
                className="px-4 py-2 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 disabled:bg-indigo-400 disabled:cursor-not-allowed"
                onClick={handleProcessRefunds}
                disabled={isProcessingRefunds}
              >
                {isProcessingRefunds ? 'Processing...' : 'Process Refunds'}
              </button>
            </div>
          ) : (
            <div className="mt-6 p-3 bg-blue-50 border border-blue-200 rounded-md">
              <p className="text-blue-700 text-sm">
                <span className="font-medium">Note:</span> Only administrators can process refunds for cancelled markets.
              </p>
            </div>
          )}
        </div>
      </div>
    );
  }

  // Show the cancellation form if the user has permission
  if (!canCancel) {
    return (
      <div className="p-6 bg-white rounded-lg shadow">
        <h2 className="text-xl font-semibold mb-4 text-gray-900">Cancel Market {marketId && `(ID: ${marketId})`}</h2>
        <div className="bg-red-50 border border-red-200 p-4 rounded-md">
          <div className="flex items-start">
            <AlertTriangle className="w-5 h-5 text-red-500 mt-0.5 mr-3 flex-shrink-0" />
            <div>
              <p className="text-red-800 font-medium">Permission Denied</p>
              <p className="text-red-700 mt-1">{permissionMessage}</p>
              {marketData.is_private && (
                <p className="text-red-700 mt-2">
                  This is a private market created by {isCreator ? 'you' : 'another user'}.
                </p>
              )}
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Default case: Show the cancellation form
  return (
    <div className="p-6 bg-white rounded-lg shadow">
      <h2 className="text-xl font-semibold mb-4 text-gray-900">Cancel Market {marketId && `(ID: ${marketId})`}</h2>
      <p className="text-gray-600">Confirm cancellation for "{marketData.title}". This action cannot be undone.</p>
      <p className="text-gray-600 mt-2">When a market is cancelled, all trade amounts and service fees will be refunded to traders.</p>
      <div className="mt-4 mb-4">
        <label htmlFor="cancel-reason" className="block text-sm font-medium text-gray-700 mb-1">Reason (Optional)</label>
        <textarea 
          id="cancel-reason" 
          rows={3}
          className="w-full p-2 border border-gray-300 rounded-md bg-white" 
          placeholder="Explain why this market is being cancelled..."
          value={cancelReason}
          onChange={(e) => setCancelReason(e.target.value)}
        />
      </div>
      <div className="mt-6">
        <button 
          className="px-4 py-2 bg-red-600 text-white rounded-md hover:bg-red-700 disabled:bg-red-400 disabled:cursor-not-allowed"
          onClick={handleCancelMarket}
          disabled={isProcessing}
        >
          {isProcessing ? 'Processing...' : 'Confirm Cancellation'}
        </button>
      </div>
    </div>
  );
} 